/**
 * NebulaShade — how a baked nebula volume turns into light along a ray (space realism, 2026-10-07). PURE.
 *
 * This is the ORACLE of NebulaVolume's TSL march: the same arithmetic in TypeScript, so a test can pin the shader to it
 * and node can draw a nebula without a GPU. Change the math here and there together.
 *
 * Along the ray, per step of length dt at a point with (gas, light, dust):
 *   line emission   j = emission · gas² · light · (colLow·(1 − hi) + colHigh·hi·highGain)
 *                   hi = light^hiPow · (1 − smoothstep(hiR0, hiR1, distance to the star))       — [O III] lives nearer the star
 *   scattered light s = scatter · dust · light^lightPow · starColor ⊙ colScatter · P(θ) / (r² + ε)   (+ dust · ambient)
 *   extinction      σ = kappa · dust · redden + gasKappa · gas                                   — per channel: dust reddens
 *   the step adds   T ⊙ (j + s) · (1 − e^(−σ dt)) / σ   and   T ← T ⊙ e^(−σ dt)
 *
 * THE SHEETS. A shock front or a planetary nebula's rim is a shell far thinner than any march step. Sampled, it would
 * be noise; instead the profile e^(−(f/w)²) of the shell's signed distance f is integrated EXACTLY over each step,
 * taking f as linear across it (`nebulaSheetIntegral`, an erf difference). A ray that crosses the shell square-on collects
 * w·√π; one that grazes it collects many times that — which is why a remnant looks like filaments.
 *
 * THE STARS inside are dimmed by the dust in FRONT of them only: the march remembers its transmittance at each star's
 * depth.
 */
import { type NebulaSpec, type NebulaVolumeData, type NebulaRGB, type NebulaV3, sampleNebulaVolume, nebulaSmooth, NEBULA_PERMS } from './NebulaField.js';

/** The softening of the star's 1/r² on dust (unit-sphere radii²): the glow is capped ~1/ε near the star. */
export const NEBULA_STAR_SOFT = 0.05;
/** A star's intrinsic angular size (rad) before the pixel floor, and the share of its light in a wide halo. */
export const NEBULA_STAR_SIGMA = 0.0012;
export const NEBULA_STAR_HALO = 0.03;
/** A star's brightness falls as 1/(d² + this) with the camera's distance d in radii. */
export const NEBULA_STAR_NEAR = 0.5;
/** A sheet is never drawn thinner than this many pixels (its light is conserved: wider, dimmer) — or it would sparkle. */
export const NEBULA_SHEET_PX = 0.75;
/** The steepest edge-on gain a sharpened sheet takes: 1/|cos θ| is capped at 1 / this. */
export const NEBULA_SHEET_COS_MIN = 0.06;

/** erf, Abramowitz & Stegun 7.1.27 (|error| < 5e-4) — cheap enough for a shader. */
export function nebulaErf(x: number): number {
  const a = Math.abs(x);
  const d = 1 + a * (0.278393 + a * (0.230389 + a * (0.000972 + a * 0.078108)));
  const d2 = d * d;
  const v = 1 - 1 / (d2 * d2);
  return x < 0 ? -v : v;
}

/** ∫₀^dt e^(−(f(t)/w)²) dt for f running linearly from f0 to f1. */
export function nebulaSheetIntegral(f0: number, f1: number, w: number, dt: number): number {
  const d = f1 - f0;
  if (Math.abs(d) < 1e-4 * w) { const m = (f0 + f1) * 0.5 / w; return dt * Math.exp(-m * m); }
  return dt * (0.886226925452758 * w) * (nebulaErf(f1 / w) - nebulaErf(f0 / w)) / d;
}

/** Henyey–Greenstein, normalised so an isotropic scatterer is 1. */
export function nebulaPhase(cos: number, g: number): number {
  const k = 1 + g * g - 2 * g * cos;
  return (1 - g * g) / (k * Math.sqrt(k));
}

/** (1 − e^(−x)) / x, stable at 0. */
export function nebulaStepWeight(x: number): number { const v = x < 1e-4 ? 1e-4 : x; return (1 - Math.exp(-v)) / v; }

/** A sheet's signed distance at p, with the wrinkle field read from the volume. */
export function nebulaSheetF(spec: NebulaSpec, vol: NebulaVolumeData, k: number, x: number, y: number, z: number): number {
  const s = spec.sheets[k], pm = NEBULA_PERMS[s.perm % NEBULA_PERMS.length], p = [x, y, z];
  const h = sampleNebulaVolume(vol.data, vol.size, p[pm[0]] * pm[3], p[pm[1]] * pm[4], p[pm[2]] * pm[5], 3);
  return Math.hypot(x / s.axes[0], y / s.axes[1], z / s.axes[2]) - s.radius - s.amp * h;
}

export interface NebulaRay { rgb: NebulaRGB; alpha: number; trans: NebulaRGB }

/**
 * March a ray (unit-sphere space: the nebula is the unit ball at the origin) through a baked volume.
 * `pixelAngle` is one pixel's angle (rad) — the stars' size floor. The detail wisps of the GPU march are not here:
 * pin the shader with its detail off.
 */
export function marchNebulaCpu(spec: NebulaSpec, vol: NebulaVolumeData, ro: NebulaV3, rd: NebulaV3, steps: number, jitter = 0.5, pixelAngle = 0.001): NebulaRay {
  const b = ro[0] * rd[0] + ro[1] * rd[1] + ro[2] * rd[2];
  const c = ro[0] * ro[0] + ro[1] * ro[1] + ro[2] * ro[2] - 1;
  const disc = b * b - c;
  const out: NebulaRay = { rgb: [0, 0, 0], alpha: 0, trans: [1, 1, 1] };
  if (disc <= 0) return out;
  const sq = Math.sqrt(disc), t0 = Math.max(-b - sq, 0), t1 = -b + sq;
  if (t1 <= t0) return out;
  const dt = (t1 - t0) / steps;
  const acc = out.rgb, T = out.trans;
  const L = spec.light, nS = spec.sheets.length;
  const fPrev: number[] = [];
  for (let k = 0; k < nS; k++) fPrev.push(nebulaSheetF(spec, vol, k, ro[0] + rd[0] * t0, ro[1] + rd[1] * t0, ro[2] + rd[2] * t0));
  const tStar = spec.stars.map((s) => (s.pos[0] - ro[0]) * rd[0] + (s.pos[1] - ro[1]) * rd[1] + (s.pos[2] - ro[2]) * rd[2]);
  const TStar: NebulaRGB[] = spec.stars.map(() => [1, 1, 1]);
  for (let i = 0; i < steps; i++) {
    const ts = t0 + i * dt, tm = ts + jitter * dt;
    const x = ro[0] + rd[0] * tm, y = ro[1] + rd[1] * tm, z = ro[2] + rd[2] * tm;
    const gas = sampleNebulaVolume(vol.data, vol.size, x, y, z, 0), light = sampleNebulaVolume(vol.data, vol.size, x, y, z, 1), dust = sampleNebulaVolume(vol.data, vol.size, x, y, z, 2);
    // where the star is from here
    let rs: number, cos = 0, inv = 1;
    if (L && L.mode === 'point') {
      const dx = x - L.pos[0], dy = y - L.pos[1], dz = z - L.pos[2];
      rs = Math.hypot(dx, dy, dz);
      cos = -(dx * rd[0] + dy * rd[1] + dz * rd[2]) / (rs || 1e-6);
      inv = 1 / (rs * rs + NEBULA_STAR_SOFT);
    } else if (L) { rs = 1; cos = L.pos[0] * rd[0] + L.pos[1] * rd[1] + L.pos[2] * rd[2]; } else rs = Math.hypot(x, y, z);
    const hi = Math.pow(light, spec.hiPow) * (1 - nebulaSmooth(spec.hiR0, spec.hiR1, rs));
    const j = spec.emission * gas * gas * light;
    const sc = L ? spec.scatter * dust * Math.pow(light, spec.lightPow) * inv * nebulaPhase(cos, spec.phaseG) * spec.starPower : 0;
    for (let ch = 0; ch < 3; ch++) {
      const e = j * (spec.colLow[ch] * (1 - hi) + spec.colHigh[ch] * hi * spec.highGain) + sc * spec.starColor[ch] * spec.colScatter[ch] + dust * spec.ambient[ch];
      const sigma = spec.kappa * dust * spec.redden[ch] + spec.gasKappa * gas;
      acc[ch] += T[ch] * e * dt * nebulaStepWeight(sigma * dt);
    }
    // the sheets, integrated across the whole step [ts, ts + dt]
    const xe = ro[0] + rd[0] * (ts + dt), ye = ro[1] + rd[1] * (ts + dt), ze = ro[2] + rd[2] * (ts + dt);
    for (let k = 0; k < nS; k++) {
      const s = spec.sheets[k], f1 = nebulaSheetF(spec, vol, k, xe, ye, ze), f0 = fPrev[k];
      const m = s.maskHi > s.maskLo ? nebulaSmooth(s.maskLo, s.maskHi, gas) : 1;
      const w = Math.max(s.width, NEBULA_SHEET_PX * pixelAngle * (ts + dt));
      // edge-on gain (NebulaSheet.sharpen): f is a signed distance, so |Δf| / dt is |cos θ| of the ray to the sheet
      const gain = (s.sharpen ?? 1) > 1 ? Math.pow(1 / Math.max(Math.abs(f1 - f0) / dt, NEBULA_SHEET_COS_MIN), (s.sharpen ?? 1) - 1) : 1;
      const nrm = s.power * m * gain / (w * 1.7724538509055159);
      const so = nebulaSheetIntegral(f0 - s.split, f1 - s.split, w, dt) * nrm, si = nebulaSheetIntegral(f0 + s.split, f1 + s.split, w, dt) * nrm;
      for (let ch = 0; ch < 3; ch++) acc[ch] += T[ch] * (s.colorOut[ch] * so + s.colorIn[ch] * si);
      fPrev[k] = f1;
    }
    for (let ch = 0; ch < 3; ch++) T[ch] *= Math.exp(-(spec.kappa * dust * spec.redden[ch] + spec.gasKappa * gas) * dt);
    for (let k = 0; k < tStar.length; k++) if (ts + dt <= tStar[k]) { TStar[k][0] = T[0]; TStar[k][1] = T[1]; TStar[k][2] = T[2]; }
  }
  // the stars inside
  for (let k = 0; k < spec.stars.length; k++) {
    if (tStar[k] <= 0) continue;
    const s = spec.stars[k];
    const vx = s.pos[0] - ro[0], vy = s.pos[1] - ro[1], vz = s.pos[2] - ro[2];
    const d2 = vx * vx + vy * vy + vz * vz, th2 = Math.max(d2 - tStar[k] * tStar[k], 0) / Math.max(d2, 1e-6);
    const sg = Math.max(NEBULA_STAR_SIGMA, pixelAngle * 0.8);
    const psf = Math.exp(-th2 / (2 * sg * sg)) * (NEBULA_STAR_SIGMA / sg) ** 2 + NEBULA_STAR_HALO * Math.exp(-th2 / (72 * sg * sg));
    const lum = s.power * psf / (d2 + NEBULA_STAR_NEAR);
    for (let ch = 0; ch < 3; ch++) acc[ch] += TStar[k][ch] * s.color[ch] * lum;
  }
  out.alpha = 1 - (T[0] + T[1] + T[2]) / 3;
  return out;
}
